/**
 * Ad beat sheet levels 1–5 for the playable shell.
 *
 * JSON sources of truth live in @afr/content-schema/levels/ (FROZEN — c0 pin).
 * C05 pacing overlays in c05-pacing/ stretch runs to 45–90s and ensure all six
 * fantasy-contract beats can land, applied at load time only.
 */

import { parseLevelOrThrow, toSimLevel } from "@afr/content-schema";
import type { LevelDef as SchemaLevelDef } from "@afr/content-schema";
import type { LevelDef } from "@afr/sim";

import raw01 from "../../../content-schema/levels/level-01.json";
import raw02 from "../../../content-schema/levels/level-02.json";
import raw03 from "../../../content-schema/levels/level-03.json";
import raw04 from "../../../content-schema/levels/level-04.json";
import raw05 from "../../../content-schema/levels/level-05.json";

import pacing01 from "./c05-pacing/level-01.json";
import pacing02 from "./c05-pacing/level-02.json";
import pacing03 from "./c05-pacing/level-03.json";
import pacing04 from "./c05-pacing/level-04.json";
import pacing05 from "./c05-pacing/level-05.json";

import {
  applyPacingOverlay,
  type PacingOverlay,
} from "./applyPacingOverlay.js";

const RAW_LEVELS = [raw01, raw02, raw03, raw04, raw05] as const;

const RAW_PACING = [
  pacing01,
  pacing02,
  pacing03,
  pacing04,
  pacing05,
] as const satisfies readonly PacingOverlay[];

/** Validated content-schema level documents (frozen base; may include schemaVersion). */
export const AD_BEAT_SCHEMA_LEVELS: readonly SchemaLevelDef[] = RAW_LEVELS.map(
  (raw) => parseLevelOrThrow(raw),
);

/** Frozen base levels (no pacing) — for tests that need raw content-schema shape. */
export const AD_BEAT_BASE_LEVELS: readonly LevelDef[] = AD_BEAT_SCHEMA_LEVELS.map(
  (level) => toSimLevel(level) as LevelDef,
);

/** C05 pacing overlays keyed by level id. */
export const C05_PACING_OVERLAYS: readonly PacingOverlay[] = RAW_PACING.map(
  (p) => p as PacingOverlay,
);

export const C05_PACING_BY_ID: Readonly<Record<string, PacingOverlay>> =
  Object.fromEntries(C05_PACING_OVERLAYS.map((p) => [p.levelId, p]));

/**
 * Sim-ready levels for createRun / RunLoop — base content-schema + C05 pacing
 * overlay (path stretch, wave pressure, pedestal/climax injection).
 */
export const AD_BEAT_LEVELS: readonly LevelDef[] = AD_BEAT_BASE_LEVELS.map(
  (base) => {
    const overlay = C05_PACING_BY_ID[base.id];
    if (!overlay) {
      throw new Error(`Missing C05 pacing overlay for ${base.id}`);
    }
    return applyPacingOverlay(base, overlay);
  },
);

export const AD_BEAT_LEVEL_IDS = AD_BEAT_LEVELS.map((l) => l.id);

/** Index 0 = L1 … index 4 = L5. */
export function getAdBeatLevel(index: number): LevelDef {
  const level = AD_BEAT_LEVELS[index];
  if (!level) {
    throw new Error(
      `Ad beat level index ${index} out of range (0–${AD_BEAT_LEVELS.length - 1})`,
    );
  }
  return level;
}

/** Lookup by id (`level-01` … `level-05`). */
export function getAdBeatLevelById(id: string): LevelDef {
  const level = AD_BEAT_LEVELS.find((l) => l.id === id);
  if (!level) {
    throw new Error(`Unknown ad beat level id: ${id}`);
  }
  return level;
}

/** Pacing overlay for a level id. */
export function getPacingOverlay(levelId: string): PacingOverlay {
  const o = C05_PACING_BY_ID[levelId];
  if (!o) {
    throw new Error(`Unknown pacing overlay for level id: ${levelId}`);
  }
  return o;
}

/**
 * Resolve level from URL query: `?level=1` … `?level=5` or `?level=level-05`.
 * Defaults to L5 (ad moment: mecha + climax) for the playable build.
 */
export function resolveLevelFromSearch(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): LevelDef {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const raw = params.get("level");
  if (!raw) {
    return getAdBeatLevel(4); // L5 default
  }
  if (/^[1-5]$/.test(raw)) {
    return getAdBeatLevel(Number(raw) - 1);
  }
  if (/^level-0[1-5]$/.test(raw)) {
    return getAdBeatLevelById(raw);
  }
  // Unknown → L5 (ad showcase)
  return getAdBeatLevel(4);
}

/** L5 convenience alias (mecha + climax). */
export const LEVEL_05 = getAdBeatLevelById("level-05");
